/**
 * 颜色转换核心库
 * 数据模型：内部统一用 RGB（0-255 整数）+ alpha（0-1）
 * 任何外部输入都先归一化到 RGBA 再分发
 */

export interface RGB {
  r: number; // 0-255
  g: number;
  b: number;
}

export interface RGBA extends RGB {
  a: number; // 0-1
}

export interface HSL {
  h: number; // 0-360
  s: number; // 0-100
  l: number; // 0-100
}

export interface HSLA extends HSL {
  a: number; // 0-1
}

// ============================================================
// 辅助：clamp、round、pad
// ============================================================

export const clamp = (n: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, n));

const round = Math.round;

const pad2 = (n: number): string => n.toString(16).padStart(2, '0');

// ============================================================
// HEX 解析
// 支持：#RGB / #RGBA / #RRGGBB / #RRGGBBAA / 无 # 前缀
// ============================================================

const HEX_RE = /^#?([0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

export function parseHex(input: string): RGBA | null {
  const s = input.trim();
  const m = HEX_RE.exec(s);
  if (!m) return null;
  let hex = m[1];
  if (hex.length === 3 || hex.length === 4) {
    hex = hex.split('').map((c) => c + c).join('');
  }
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  const a = hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1;
  if ([r, g, b].some(Number.isNaN)) return null;
  return { r, g, b, a };
}

export function toHex({ r, g, b, a }: RGBA, withAlpha = false): string {
  const base = `#${pad2(r)}${pad2(g)}${pad2(b)}`;
  if (!withAlpha) return base.toUpperCase();
  const aa = pad2(round(clamp(a, 0, 1) * 255));
  return (base + aa).toUpperCase();
}

// ============================================================
// RGB(A) 字符串解析
// 支持：rgb(255, 99, 71) / rgba(255, 99, 71, 0.5) / rgb(255 99 71)
// ============================================================

const RGB_RE =
  /^rgba?\(\s*(-?\d+(?:\.\d+)?%?)\s*[,\s]\s*(-?\d+(?:\.\d+)?%?)\s*[,\s]\s*(-?\d+(?:\.\d+)?%?)(?:\s*[,/]\s*(-?\d+(?:\.\d+)?%?))?\s*\)$/i;

export function parseRgb(input: string): RGBA | null {
  const m = RGB_RE.exec(input.trim());
  if (!m) return null;
  const parseChannel = (v: string): number => {
    if (v.endsWith('%')) return clamp((parseFloat(v) / 100) * 255, 0, 255);
    return clamp(parseFloat(v), 0, 255);
  };
  const r = parseChannel(m[1]);
  const g = parseChannel(m[2]);
  const b = parseChannel(m[3]);
  let a = 1;
  if (m[4] !== undefined) {
    const av = m[4];
    a = av.endsWith('%') ? parseFloat(av) / 100 : parseFloat(av);
    a = clamp(a, 0, 1);
  }
  return { r: round(r), g: round(g), b: round(b), a };
}

// ============================================================
// HSL(A) 字符串解析
// 支持：hsl(9, 100%, 64%) / hsla(9, 100%, 64%, 0.5) / hsl(9 100% 64%)
// ============================================================

const HSL_RE =
  /^hsla?\(\s*(-?\d+(?:\.\d+)?)(?:deg)?\s*[,\s]\s*(-?\d+(?:\.\d+)?)%\s*[,\s]\s*(-?\d+(?:\.\d+)?)%(?:\s*[,/]\s*(-?\d+(?:\.\d+)?%?))?\s*\)$/i;

export function parseHsl(input: string): RGBA | null {
  const m = HSL_RE.exec(input.trim());
  if (!m) return null;
  const h = ((parseFloat(m[1]) % 360) + 360) % 360;
  const s = clamp(parseFloat(m[2]), 0, 100);
  const l = clamp(parseFloat(m[3]), 0, 100);
  let a = 1;
  if (m[4] !== undefined) {
    a = m[4].endsWith('%') ? parseFloat(m[4]) / 100 : parseFloat(m[4]);
    a = clamp(a, 0, 1);
  }
  return hslToRgb({ h, s, l, a });
}

// ============================================================
// RGB ↔ HSL 转换
// ============================================================

export function rgbToHsl({ r, g, b, a }: RGBA): HSLA {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rn:
        h = (gn - bn) / d + (gn < bn ? 6 : 0);
        break;
      case gn:
        h = (bn - rn) / d + 2;
        break;
      case bn:
        h = (rn - gn) / d + 4;
        break;
    }
    h *= 60;
  }
  return { h: round(h), s: round(s * 100), l: round(l * 100), a };
}

export function hslToRgb({ h, s, l, a }: HSLA): RGBA {
  const hn = ((h % 360) + 360) % 360 / 360;
  const sn = clamp(s, 0, 100) / 100;
  const ln = clamp(l, 0, 100) / 100;

  if (sn === 0) {
    const v = round(ln * 255);
    return { r: v, g: v, b: v, a };
  }

  const hue2rgb = (p: number, q: number, t: number): number => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };

  const q = ln < 0.5 ? ln * (1 + sn) : ln + sn - ln * sn;
  const p = 2 * ln - q;
  return {
    r: round(hue2rgb(p, q, hn + 1 / 3) * 255),
    g: round(hue2rgb(p, q, hn) * 255),
    b: round(hue2rgb(p, q, hn - 1 / 3) * 255),
    a
  };
}

// ============================================================
// 格式化输出
// ============================================================

export function toRgb({ r, g, b, a }: RGBA): string {
  if (a >= 1) return `rgb(${r}, ${g}, ${b})`;
  return `rgba(${r}, ${g}, ${b}, ${round(a * 100) / 100})`;
}

export function toHsl({ r, g, b, a }: RGBA): string {
  const { h, s, l } = rgbToHsl({ r, g, b, a });
  if (a >= 1) return `hsl(${h}, ${s}%, ${l}%)`;
  return `hsla(${h}, ${s}%, ${l}%, ${round(a * 100) / 100})`;
}

export function toHsv(rgba: RGBA): { h: number; s: number; v: number } {
  const { r, g, b } = rgba;
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const d = max - min;
  let h = 0;
  const s = max === 0 ? 0 : d / max;
  const v = max;
  if (d !== 0) {
    switch (max) {
      case rn:
        h = (gn - bn) / d + (gn < bn ? 6 : 0);
        break;
      case gn:
        h = (bn - rn) / d + 2;
        break;
      case bn:
        h = (rn - gn) / d + 4;
        break;
    }
    h *= 60;
  }
  return { h: round(h), s: round(s * 100), v: round(v * 100) };
}

// ============================================================
// 通用解析：任何输入字符串 → RGBA（无法解析返回 null）
// ============================================================

export function parseColor(input: string): RGBA | null {
  const s = input.trim();
  if (s.startsWith('#')) return parseHex(s);
  if (s.startsWith('rgb')) return parseRgb(s);
  if (s.startsWith('hsl')) return parseHsl(s);
  // 兜底：当 HEX 解析（无 #）
  if (/^[0-9a-f]{3,8}$/i.test(s)) return parseHex('#' + s);
  return null;
}

// ============================================================
// 工具：判断深色 / 浅色
// ============================================================

export function isDark({ r, g, b }: RGB): boolean {
  // YIQ 亮度
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq < 128;
}

export function relativeLuminance({ r, g, b }: RGB): number {
  const norm = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * norm(r) + 0.7152 * norm(g) + 0.0722 * norm(b);
}

// ============================================================
// 工具：随机色（HSL 走，避免太灰）
// ============================================================

export function randomColor(alpha = 1): RGBA {
  const h = Math.random() * 360;
  const s = 60 + Math.random() * 40; // 60-100%
  const l = 40 + Math.random() * 25; // 40-65%
  return hslToRgb({ h, s, l, a: alpha });
}

// ============================================================
// PNG 导出
// ============================================================

export function exportColorAsPng(c: RGBA): void {
  const size = 512;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  // 背景色
  ctx.fillStyle = `rgb(${c.r}, ${c.g}, ${c.b})`;
  ctx.fillRect(0, 0, size, size);

  // 底部信息栏
  ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
  ctx.fillRect(0, size - 80, size, 80);

  // HEX 文字
  const hex = toHex(c);
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 48px ui-monospace, SF Mono, Menlo, monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(hex, size / 2, size - 40);

  // 下载
  const link = document.createElement('a');
  link.download = `huefetch-${hex.replace('#', '')}.png`;
  link.href = canvas.toDataURL('image/png');
  link.click();
}
